/**
 * Purpose: Shows native OS notifications when the page is not visible
 * but socket.io is connected (app open, tab in background).
 *
 * How it works:
 * 1. Checks Notification.permission — only shows if "granted"
 * 2. If SW registration is available, uses registration.showNotification()
 * 3. Otherwise, falls back to new Notification() with click-to-navigate
 *
 * Key features:
 * - Uses SW registration for proper banner display on macOS / all platforms
 * - Falls back to new Notification() in dev mode (no SW)
 * - Click handling navigates to the chat via setActiveChatId
 * - Muted chat and outgoing message filtering handled by callers
 */

import { createLogger } from "./debug";

const log = createLogger("AppNotification");

// Keep references to fallback Notification objects so they aren't garbage-collected
// (GC would silently drop onclick / onclose handlers).
// Capped at MAX_ACTIVE to prevent unbounded growth if onclose doesn't fire reliably.
const MAX_ACTIVE_NOTIFICATIONS = 50;
const activeNotifications: Record<string, Notification> = {};

function pruneActiveNotifications(): void {
  if (Object.keys(activeNotifications).length <= MAX_ACTIVE_NOTIFICATIONS) {
    return;
  }
  // Remove oldest entries (first inserted) until within limit
  const keys = Object.keys(activeNotifications);
  const excess = keys.length - MAX_ACTIVE_NOTIFICATIONS;
  for (let i = 0; i < excess; i++) {
    delete activeNotifications[keys[i]];
  }
}

/**
 * Show a fallback notification using new Notification() when SW is not registered.
 * Handles click-to-navigate via setActiveChatId.
 */
function showFallbackNotification(
  title: string,
  body: string,
  icon: string,
  tag: string,
  chatId: string | null,
): void {
  let fallback: Notification;
  try {
    fallback = new Notification(title, { body, icon, tag });
  } catch (error) {
    log.error("Failed to create fallback Notification", { error });
    return;
  }

  // Store reference at module level to prevent garbage collection
  activeNotifications[tag] = fallback;
  pruneActiveNotifications();

  const targetUrl = chatId
    ? `${globalThis.location.origin}/${chatId}`
    : `${globalThis.location.origin}/`;

  fallback.onclick = () => {
    globalThis.focus();

    // Notify the main app that a notification was clicked so it can handle navigation.
    // Uses postMessage to avoid circular imports with NavigationState.
    try {
      globalThis.postMessage(
        {
          source: "app-notification",
          type: "notification-click",
          chatId,
          url: targetUrl,
        },
        globalThis.location.origin,
      );
    } catch (error) {
      // Fallback: use direct navigation if postMessage fails
      log.error("postMessage failed, using direct navigation", { error });
      globalThis.location.href = targetUrl;
    }

    fallback.close();
    delete activeNotifications[tag];
  };

  fallback.onclose = () => {
    delete activeNotifications[tag];
  };
}

export interface AppNotificationParams {
  title: string;
  body: string;
  chatId: string | null;
  tag: string;
  icon?: string;
  /** Notification type for the SW click handler (e.g. "Message", "Meeting") */
  type?: string;
}

/**
 * Show a notification via the service worker registration.
 * Uses registration.showNotification() so the OS treats it as a proper
 * notification with banners/alerts (unlike new Notification() which macOS
 * may suppress to the notification center only).
 */
export async function showAppNotification(
  params: AppNotificationParams,
): Promise<void> {
  log.info("showAppNotification called", {
    title: params.title,
    tag: params.tag,
  });

  // Skip if notifications are not permitted
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  ) {
    log.warn("Skipped: Notification permission is not granted", {
      permission:
        typeof Notification === "undefined"
          ? "Notification API unavailable"
          : Notification.permission,
    });
    return;
  }

  // Try to get SW registration (not available in dev mode)
  let registration: ServiceWorkerRegistration | undefined;
  if ("serviceWorker" in navigator) {
    try {
      registration = await navigator.serviceWorker.getRegistration();
    } catch (error) {
      log.warn(
        "Service worker registration lookup failed, using fallback notification",
        { error },
      );
      registration = undefined;
    }
  }

  const { title, body, chatId, tag, icon, type } = params;

  // Get existing notifications with same tag for iOS compatibility
  let existingNotifications: Notification[] = [];
  if (registration) {
    existingNotifications = await registration.getNotifications({ tag });
  }

  // Build body — use simple singular/plural logic for iOS compatibility
  // Only apply to Message type notifications, keep original body for others
  const finalBody =
    type === "Message" && existingNotifications.length > 0
      ? "New messages"
      : body || "New message";

  // Close existing notifications before showing updated one
  // This is critical for iOS compatibility since iOS ignores tag replacement
  existingNotifications.forEach((notification: Notification) =>
    notification.close(),
  );

  const defaultIcon = `${globalThis.location.origin}/logo/192.png`;

  // Pass the same data shape the SW notificationclick handler expects
  // so clicks are handled automatically (focus window + navigate to chat)
  try {
    const notificationOptions: NotificationOptions = {
      body: finalBody,
      icon: icon || defaultIcon,
      badge: `${globalThis.location.origin}/logo/badge-72.png`,
      tag,
      renotify: true,
      vibrate: [100, 50, 100],
      data: {
        type: type || "Message",
        chatId,
        url: chatId
          ? `${globalThis.location.origin}/${chatId}`
          : `${globalThis.location.origin}/`,
      },
    };

    if (registration) {
      await registration.showNotification(title, notificationOptions);
    } else {
      showFallbackNotification(
        title,
        finalBody,
        icon || defaultIcon,
        tag,
        chatId,
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to show app notification:", error);
  }
}

/**
 * Show a message notification when the app is in the background.
 *
 * @param senderName - Display name of the sender
 * @param groupName - Group chat name (null for P2P chats)
 * @param chatId - The chat ID for navigation
 */
export async function showMessageNotification(
  senderName: string,
  groupName: string | null,
  chatId: string,
): Promise<void> {
  const title = groupName ? `${senderName} in ${groupName}` : senderName;
  const tag = `${chatId}_${senderName}`;

  log.info("showMessageNotification called", {
    senderName,
    groupName,
    chatId,
    title,
    tag,
  });

  await showAppNotification({
    title,
    body: "New message", // This will be updated to "New messages" for subsequent notifications
    chatId,
    tag,
    type: "Message",
  });
}

/**
 * Show a meeting-started notification when the app is in the background.
 *
 * @param chatName - Name of the chat/meeting
 * @param chatId - The chat ID for navigation
 * @param chatType - "GROUP" or "P2P" to set the correct notification type
 */
export async function showMeetingNotification(
  chatName: string,
  chatId: string,
  chatType: "GROUP" | "P2P",
): Promise<void> {
  // Use the same tag scheme as the SW push notification parsers:
  // GROUP → "meeting_{chatId}", P2P → "meeting_p2p_{chatId}"
  const tag =
    chatType === "P2P" ? `meeting_p2p_${chatId}` : `meeting_${chatId}`;

  await showAppNotification({
    title:
      chatType === "P2P"
        ? `Incoming call: ${chatName}`
        : `Meeting started: ${chatName}`,
    body: "Tap to join",
    chatId,
    tag,
    type: chatType === "P2P" ? "MeetingP2P" : "Meeting",
  });
}

/**
 * Close meeting notifications for a given chat/room ID.
 * Uses the SW registration to find and close notifications by tag.
 */
export async function closeMeetingNotification(chatId: string): Promise<void> {
  // Clear active notification references for both tag variants
  delete activeNotifications[`meeting_${chatId}`];
  delete activeNotifications[`meeting_p2p_${chatId}`];

  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }

    // Close both GROUP and P2P meeting notifications for this chat
    const [groupNotifs, p2pNotifs] = await Promise.all([
      registration.getNotifications({ tag: `meeting_${chatId}` }),
      registration.getNotifications({ tag: `meeting_p2p_${chatId}` }),
    ]);
    groupNotifs.forEach((n) => n.close());
    p2pNotifs.forEach((n) => n.close());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to close meeting notification:", error);
  }
}
